/*
 * AriaOne API Service Layer
 * ─────────────────────────
 * Central service for all backend API calls.
 * Currently returns MOCKED data. When ready to go live,
 * uncomment the real fetch calls and remove the mock returns.
 *
 * Environment: REACT_APP_BACKEND_URL
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/* ── HTTP helpers ── */
const headers = () => {
  const h = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('ariaone-token');
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
};

let _refreshing = null;

const tryRefreshOnce = async () => {
  if (_refreshing) return _refreshing;
  const rt = localStorage.getItem('ariaone-refresh-token');
  if (!rt) return false;
  _refreshing = (async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem('ariaone-token', data.access_token);
        if (data.refresh_token) localStorage.setItem('ariaone-refresh-token', data.refresh_token);
        return true;
      }
      return false;
    } catch { return false; }
    finally { _refreshing = null; }
  })();
  return _refreshing;
};

const request = async (method, path, body) => {
  const res = await fetch(`${API_URL}${path}`, {
    method, headers: headers(),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (res.status === 429) throw new Error('Too many requests, please wait and try again.');
  // On 401: try refresh once, retry once
  if (res.status === 401) {
    const refreshed = await tryRefreshOnce();
    if (refreshed) {
      const retry = await fetch(`${API_URL}${path}`, {
        method, headers: headers(),
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      if (retry.status === 429) throw new Error('Too many requests, please wait and try again.');
      const rd = await retry.json().catch(() => ({}));
      if (!retry.ok) throw new Error(rd.detail || `API error ${retry.status}`);
      return rd;
    }
    localStorage.removeItem('ariaone-token');
    localStorage.removeItem('ariaone-refresh-token');
    throw new Error('Session expired \u2014 please sign in again');
  }
  if (!res.ok) {
    let msg = `API error ${res.status}`;
    try {
      const errData = await res.json();
      const d = errData.detail;
      if (typeof d === 'string') msg = d;
      else if (d?.message) msg = d.message;
      else if (errData.message) msg = errData.message;
    } catch { /* body unreadable */ }
    throw new Error(msg);
  }
  return res.json();
};

const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  put: (path, body) => request('PUT', path, body),
  del: (path) => request('DELETE', path),
};

/* ════════════════════════════════════════
   AUTH — Template for JWT auth flow
   ════════════════════════════════════════ */
export const AuthService = {
  // POST /api/auth/login
  login: async (email, password) => {
    return api.post('/api/auth/login', { email, password });
  },
  // POST /api/auth/register
  register: async (data) => {
    return api.post('/api/auth/register', data);
  },
  // GET /api/auth/me
  getProfile: async () => {
    return api.get('/api/auth/me');
  },
  // POST /api/auth/refresh-token
  refreshToken: async (refresh_token) => {
    return api.post('/api/auth/refresh-token', { refresh_token });
  },
};

/* ════════════════════════════════════════
   LUMINA — Clinical Documentation & AI
   ════════════════════════════════════════ */
export const LuminaService = {
  // GET /api/lumina/patients
  getPatients: async () => {
    // return api.get('/api/lumina/patients');
    return [];
  },
  // GET /api/lumina/patients/:id
  getPatient: async (id) => {
    // return api.get(`/api/lumina/patients/${id}`);
    return null;
  },
  // POST /api/lumina/soap/generate — GPT-5.2 integration ready
  generateSOAP: async (patientId, transcript) => {
    return api.post('/api/lumina/soap/generate', { patient_id: patientId, transcript });
  },
  // POST /api/lumina/soap/sign
  signNote: async (noteId) => {
    // return api.post(`/api/lumina/soap/${noteId}/sign`);
    return { success: true };
  },
  // POST /api/lumina/lens/extract — Lens OCR + AI extraction
  lensExtract: async (file) => {
    // const formData = new FormData(); formData.append('file', file);
    // return fetch(`${API_URL}/api/lumina/lens/extract`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('ariaone-token')}` }, body: formData }).then(r => r.json());
    return { fields: [], confidence: 95 };
  },
  // POST /api/lumina/smart-prep/generate — AI prep suggestions (persists to DB)
  generateSmartPrep: async (patientContext, refresh = false) => {
    const url = refresh ? '/api/lumina/smart-prep/generate?refresh=true' : '/api/lumina/smart-prep/generate';
    return api.post(url, patientContext);
  },
  // GET /api/lumina/smart-prep/{patient_id} — fetch latest cached prep
  getSmartPrepCached: async (patientId) => {
    return api.get(`/api/lumina/smart-prep/${patientId}`);
  },
  // GET /api/lumina/smart-prep/{patient_id}/history — fetch prep history
  getSmartPrepHistory: async (patientId, limit = 10) => {
    return api.get(`/api/lumina/smart-prep/${patientId}/history?limit=${limit}`);
  },
  // POST /api/lumina/schedule/scan — Upload schedule file for extraction (persists to DB)
  scanSchedule: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('ariaone-token');
    const h = {};
    if (token) h['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_URL}/api/lumina/schedule/scan`, {
      method: 'POST',
      headers: h,
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: `Error ${res.status}` }));
      throw new Error(err.detail || `Schedule scan failed (${res.status})`);
    }
    return res.json();
  },
  // GET /api/lumina/schedule/latest — fetch most recent persisted schedule
  getLatestSchedule: async () => {
    return api.get('/api/lumina/schedule/latest');
  },
  // GET /api/lumina/schedule/imports — list schedule imports
  getScheduleImports: async (limit = 10) => {
    return api.get(`/api/lumina/schedule/imports?limit=${limit}`);
  },
  // GET /api/lumina/audit-log — workflow audit log
  getAuditLog: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/api/lumina/audit-log${qs ? '?' + qs : ''}`);
  },
  // POST /api/lumina/chart-integrity/{id} — AI chart integrity
  getChartIntegrity: async (patientId, patientContext = {}) => {
    return api.post(`/api/lumina/chart-integrity/${patientId}`, patientContext);
  },
  // POST /api/lumina/hcc-detection/{id} — AI HCC detection
  getHCCDetection: async (patientId, patientContext = {}) => {
    return api.post(`/api/lumina/hcc-detection/${patientId}`, patientContext);
  },
  // POST /api/lumina/medication-check/{id} — AI medication safety
  getMedicationCheck: async (patientId, patientContext = {}) => {
    return api.post(`/api/lumina/medication-check/${patientId}`, patientContext);
  },
  // GET /api/lumina/appointments
  getAppointments: async (date) => {
    // return api.get(`/api/lumina/appointments?date=${date}`);
    return [];
  },
  // POST /api/lumina/room-patient — Cross-app sync
  roomPatient: async (patientId, room, workflow) => {
    // return api.post('/api/lumina/room-patient', { patient_id: patientId, room, workflow });
    return { success: true };
  },
  // POST /api/lumina/send-to-billing — Cross-app sync to RevQ
  sendToBilling: async (noteId, patientId) => {
    // return api.post('/api/lumina/send-to-billing', { note_id: noteId, patient_id: patientId });
    return { claim_id: `CLM-${Date.now()}`, success: true };
  },
};

/* ════════════════════════════════════════
   REVQ — Revenue Cycle Management
   ════════════════════════════════════════ */
export const RevQService = {
  // GET /api/revq/dashboard
  getDashboard: async () => {
    // return api.get('/api/revq/dashboard');
    return {};
  },
  // GET /api/revq/claims
  getClaims: async (filters) => {
    // return api.get(`/api/revq/claims?${new URLSearchParams(filters)}`);
    return [];
  },
  // POST /api/revq/claims/:id/status
  updateClaimStatus: async (claimId, status) => {
    // return api.post(`/api/revq/claims/${claimId}/status`, { status });
    return { success: true };
  },
  // GET /api/revq/denials
  getDenials: async () => {
    // return api.get('/api/revq/denials');
    return [];
  },
  // POST /api/revq/ai-scrub — AI claims scrubbing
  aiScrubClaim: async (claimId) => {
    // return api.post(`/api/revq/claims/${claimId}/ai-scrub`);
    return { suggestions: [], risk_score: 0 };
  },
  // POST /api/revq/appeal/generate — AI appeal letter generation
  generateAppeal: async (denialId) => {
    // return api.post(`/api/revq/denials/${denialId}/appeal`);
    return { letter: '' };
  },
  // GET /api/revq/analytics
  getAnalytics: async (dateRange) => {
    // return api.get(`/api/revq/analytics?${new URLSearchParams(dateRange)}`);
    return {};
  },
};

/* ════════════════════════════════════════
   ECHO — Front Office Operations
   ════════════════════════════════════════ */
export const EchoService = {
  // GET /api/echo/dashboard
  getDashboard: async () => {
    // return api.get('/api/echo/dashboard');
    return {};
  },
  // GET /api/echo/schedule
  getSchedule: async (date) => {
    // return api.get(`/api/echo/schedule?date=${date}`);
    return [];
  },
  // POST /api/echo/check-in — Cross-app sync to Lumina
  checkInPatient: async (patientId) => {
    // return api.post('/api/echo/check-in', { patient_id: patientId });
    return { success: true };
  },
  // GET /api/echo/patient-flow
  getPatientFlow: async () => {
    // return api.get('/api/echo/patient-flow');
    return [];
  },
  // POST /api/echo/ai-agent/configure
  configureAgent: async (agentId, config) => {
    // return api.post(`/api/echo/ai-agents/${agentId}/configure`, config);
    return { success: true };
  },
  // GET /api/echo/call-logs
  getCallLogs: async () => {
    // return api.get('/api/echo/call-logs');
    return [];
  },
  // POST /api/echo/schedule/update — Cross-app sync
  updateSchedule: async (appointmentId, changes) => {
    // return api.post(`/api/echo/schedule/${appointmentId}`, changes);
    return { success: true };
  },
};

/* ════════════════════════════════════════
   SHARED — Cross-app services
   ════════════════════════════════════════ */
export const SharedService = {
  // GET /api/notifications
  getNotifications: async () => {
    // return api.get('/api/notifications');
    return [];
  },
  // POST /api/notifications/read
  markRead: async (notifId) => {
    // return api.post(`/api/notifications/${notifId}/read`);
    return { success: true };
  },
  // GET /api/users
  getUsers: async () => {
    // return api.get('/api/users');
    return [];
  },
  // POST /api/users/invite
  inviteUser: async (data) => {
    // return api.post('/api/users/invite', data);
    return { success: true };
  },
  // PUT /api/users/:id/role
  updateUserRole: async (userId, role) => {
    // return api.put(`/api/users/${userId}/role`, { role });
    return { success: true };
  },
  // POST /api/lens/upload — Object storage upload
  uploadDocument: async (file, metadata) => {
    // const formData = new FormData();
    // formData.append('file', file);
    // formData.append('metadata', JSON.stringify(metadata));
    // return fetch(`${API_URL}/api/lens/upload`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('ariaone-token')}` }, body: formData }).then(r => r.json());
    return { url: '', id: `doc-${Date.now()}` };
  },
};

/* ════════════════════════════════════════
   LLM — AI Integration Templates
   ════════════════════════════════════════ */
export const LLMService = {
  // Text generation (GPT-5.2 via emergentintegrations)
  generateText: async (prompt, context) => {
    return api.post('/api/ai/generate', { prompt, context });
  },
  // Image analysis (GPT Image 1 / Nano Banana)
  analyzeImage: async (imageUrl, prompt) => {
    return api.post('/api/ai/analyze-image', { image_url: imageUrl, prompt });
  },
  // Transcription (Whisper)
  transcribe: async (audioBlob) => {
    // const formData = new FormData();
    // formData.append('audio', audioBlob);
    // return fetch(`${API_URL}/api/ai/transcribe`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('ariaone-token')}` }, body: formData }).then(r => r.json());
    return { text: '' };
  },
  // Text-to-Speech (OpenAI TTS)
  textToSpeech: async (text, voice) => {
    return api.post('/api/ai/tts', { text, voice: voice || 'nova' });
  },
};

export default api;

/* ════════════════════════════════════════
   FHIR — EHR Integration (Read + Write)
   ════════════════════════════════════════ */
export const FhirService = {
  health: () => api.get('/api/fhir/health'),
  getPatientSummary: (patientId) => api.get(`/api/fhir/patient/${patientId}/summary`),
  syncPatient: (patientId) => api.post(`/api/fhir/patient/${patientId}/sync`),
  getSyncStatus: (patientId) => api.get(`/api/fhir/patient/${patientId}/sync-status`),
  getWriteConfig: (role) => api.get('/api/fhir/write-config', { headers: { 'X-User-Role': role } }),
  writeProblem: (patientId, data, role, dryRun = false) =>
    api.post(`/api/fhir/patient/${patientId}/problems${dryRun ? '?dry_run=true' : ''}`, data, { headers: { 'X-User-Role': role } }),
  writeMedication: (patientId, data, role, dryRun = false) =>
    api.post(`/api/fhir/patient/${patientId}/medications${dryRun ? '?dry_run=true' : ''}`, data, { headers: { 'X-User-Role': role } }),
  writeAllergy: (patientId, data, role, dryRun = false) =>
    api.post(`/api/fhir/patient/${patientId}/allergies${dryRun ? '?dry_run=true' : ''}`, data, { headers: { 'X-User-Role': role } }),
  writeNote: (patientId, data, role, dryRun = false) =>
    api.post(`/api/fhir/patient/${patientId}/notes${dryRun ? '?dry_run=true' : ''}`, data, { headers: { 'X-User-Role': role } }),
};


// ═══════════════════════════════════════
// Dictation Service — Audio capture sessions
// ═══════════════════════════════════════
export const DictationService = {
  getStatus: () => api.get('/api/dictation/status'),

  createSession: (data) => api.post('/api/dictation/sessions', data),

  updateSession: (sessionId, action) =>
    api.put(`/api/dictation/sessions/${sessionId}`, { action }),

  uploadChunk: async (sessionId, audioBlob, chunkIndex, durationMs) => {
    const formData = new FormData();
    const ext = audioBlob.type?.includes('webm') ? 'webm' : audioBlob.type?.includes('ogg') ? 'ogg' : 'webm';
    formData.append('audio', audioBlob, `chunk_${chunkIndex}.${ext}`);
    formData.append('chunk_index', String(chunkIndex));
    formData.append('duration_ms', String(durationMs));

    const token = localStorage.getItem('ariaone-token');
    const h = {};
    if (token) h['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${API_URL}/api/dictation/sessions/${sessionId}/chunks`, {
      method: 'POST',
      headers: h,
      body: formData,
    });
    if (!res.ok) throw new Error(`Chunk upload failed: ${res.status}`);
    return res.json();
  },

  finalizeSession: (sessionId) =>
    api.post(`/api/dictation/sessions/${sessionId}/finalize`),

  getSession: (sessionId) => api.get(`/api/dictation/sessions/${sessionId}`),

  listSessions: (limit = 20) => api.get(`/api/dictation/sessions?limit=${limit}`),
};
